import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, createTheme, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FinFormProps } from './types';
import { FieldRenderer } from './FieldRenderer';
import { generateSchema } from './generateSchema';
import { StepNavigation } from './StepNavigation';
import { CustomButtons } from './CustomButtons';
import { ButtonGroup } from './ButtonGroup';

// Create a QueryClient instance with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 60 * 1000, // 15 minutes (was 5)
      gcTime: 30 * 60 * 1000, // 30 minutes (was 10)
      retry: 3, // Increase retries (was 1)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

export const FinForm: React.FC<FinFormProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <FinFormInner {...props} />
    </QueryClientProvider>
  );
};

const FinFormInner: React.FC<FinFormProps> = ({
  id,
  theme,
  fields,
  onSubmit,
  submitButtonText = 'Submit',
  onSubmitClick,
  defaultValues,
  showSubmitButton = true,
  buttons,
  buttonGroup,
  // API configuration
  baseUrl,
  apiHeaders,
}) => {
  // Auto-detect if this is a multi-step form based on fields configuration
  const stepNumbers = [...new Set(fields.map(field => field.step).filter(step => step !== undefined && step !== null && step > 0))].sort((a, b) => (a || 0) - (b || 0));
  const isMultiStep = stepNumbers.length > 1;
  const totalSteps = stepNumbers.length || 1;

  // Internal multi-step state
  const [currentStep, setCurrentStep] = useState(1);

  // Get current step fields
  const currentStepFields = isMultiStep 
    ? fields.filter(field => field.step === currentStep)
    : fields;

  // Generate schema for current step only (if multi-step)
  const schema = generateSchema(currentStepFields);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues,
  });

  // Watch all form values for onChange callback
  const watchedValues = watch();

  // Validate current step
  const validateCurrentStep = async (): Promise<boolean> => {
    if (!isMultiStep) return true;
    
    const currentFields = fields.filter(field => field.step === currentStep);
    
    for (const field of currentFields) {
      if (field.required) {
        const value = watchedValues[field.name];
        let isValid = true;
        
        if (field.type === 'checkbox') {
          isValid = value === true;
        } else {
          isValid = value !== undefined && value !== null && value !== '';
        }
        
        if (!isValid) {
          return false;
        }
      }
    }
    
    // Also check form validation errors
    const formErrors = await trigger();
    if (!formErrors) {
      const currentStepFormErrors = Object.keys(errors)
        .filter(key => currentFields.some(field => field.name === key))
        .map(key => {
          const error = errors[key];
          return typeof error?.message === 'string' ? error.message : `${key} is invalid`;
        });
      return currentStepFormErrors.length === 0;
    }
    
    return true;
  };

  // Check if current step is valid (synchronous version for button state)
  const isCurrentStepValid = useMemo(() => {
    if (!isMultiStep) return true;
    
    const currentFields = fields.filter(field => field.step === currentStep);
    
    for (const field of currentFields) {
      if (field.required) {
        const value = watchedValues[field.name];
        let isValid = true;
        
        if (field.type === 'checkbox') {
          isValid = value === true;
        } else {
          isValid = value !== undefined && value !== null && value !== '';
        }
        
        if (!isValid) {
          return false;
        }
      }
    }
    
    // Check for form validation errors
    const currentStepFormErrors = Object.keys(errors)
      .filter(key => currentFields.some(field => field.name === key));
    
    return currentStepFormErrors.length === 0;
  }, [isMultiStep, currentStep, fields, watchedValues, errors]);

  // Step navigation handler
  const handleStepChange = async (step: number) => {
    if (step < 1 || step > totalSteps) return;
    
    // If moving to next step, validate current step first
    if (step > currentStep && isMultiStep) {
      const isCurrentStepValid = await validateCurrentStep();
      if (!isCurrentStepValid) {
        return; // Don't proceed if current step is not valid
      }
    }
    
    setCurrentStep(step);
  };

  // Handle form submission
  const handleFormSubmit = async (data: any) => {
    // Prevent form submission if we're in a multi-step form and not on the final step
    if (isMultiStep && currentStep < totalSteps) {
      // This should not happen with proper button types, but just in case
      console.warn('Form submission attempted on non-final step');
      return;
    }
    
    // Final step or single-step form - submit the form
    if (onSubmitClick) {
      onSubmitClick(data);
    }
    onSubmit(data);
  };

  // Handle step navigation (separate from form submission)
  const handleNextStep = async (e?: React.MouseEvent) => {
    // Prevent any default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isMultiStep && currentStep < totalSteps) {
      const isValid = await validateCurrentStep();
      if (isValid) {
        handleStepChange(currentStep + 1);
      }
    }
  };

  // Handle step navigation (separate from form submission)
  const handlePreviousStep = (e?: React.MouseEvent) => {
    // Prevent any default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isMultiStep && currentStep > 1) {
      handleStepChange(currentStep - 1);
    }
  };

  // Helper function to get grid column span
  const getColumnSpan = (field: any) => {
    // Priority: col > responsive breakpoints > default 12
    if (field.col) {
      return field.col;
    } else if (field.xs || field.sm || field.md || field.lg || field.xl) {
      // For now, use xs as the base (can be extended for responsive)
      return field.xs || field.sm || field.md || field.lg || field.xl || 12;
    } else {
      // Default to full width
      return 12;
    }
  };

  // Create custom theme if provided
  const customTheme = theme ? createTheme({
    palette: {
      primary: {
        main: theme.primaryColor || '#1976d2',
      },
      secondary: {
        main: theme.secondaryColor || '#dc004e',
      },
      background: {
        default: theme.backgroundColor || '#ffffff',
      },
      text: {
        primary: theme.textColor || '#000000',
      },
    },
    shape: {
      borderRadius: theme.borderRadius || 4,
    },
    spacing: theme.spacing || 8,
    typography: {
      fontFamily: theme.typography?.fontFamily || 'Roboto, Arial, sans-serif',
      fontSize: typeof theme.typography?.fontSize === 'number' ? theme.typography.fontSize : 14,
    },
  }) : undefined;

  // Render buttons
  const renderButtons = () => {
    // ButtonGroup takes precedence over individual buttons
    if (buttonGroup && buttonGroup.buttons && buttonGroup.buttons.length > 0) {
      return (
        <Box sx={{ mt: 3 }}>
          <ButtonGroup
            config={buttonGroup}
            onSubmit={handleFormSubmit}
            onStepChange={handleStepChange}
            currentStep={currentStep}
            totalSteps={totalSteps}
            isMultiStep={isMultiStep}
            formData={watchedValues}
          />
        </Box>
      );
    }

    if (buttons && buttons.length > 0) {
      return (
        <CustomButtons
          buttons={buttons}
        />
      );
    }

    if (!showSubmitButton) {
      return null;
    }

    return (
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isMultiStep && currentStep > 1 && (
            <Button
              type="button"
              variant="outlined"
              onClick={handlePreviousStep}
            >
              Previous
            </Button>
          )}
          
          {isMultiStep ? (
            // Multi-step form: Show Next for non-final steps, Submit for final step
            currentStep < totalSteps ? (
              <Button
                type="button"
                variant="contained"
                color="primary"
                size="large"
                disabled={!isCurrentStepValid}
                onClick={handleNextStep}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!isCurrentStepValid}
              >
                {submitButtonText}
              </Button>
            )
          ) : (
            // Single-step form: Show Submit button
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
            >
              {submitButtonText}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const formContent = (
    <Box 
      component="form" 
      onSubmit={(e) => {
        // Prevent form submission if not on final step
        if (isMultiStep && currentStep < totalSteps) {
          e.preventDefault();
          e.stopPropagation();
          // Optionally, trigger next step if current step is valid
          if (isCurrentStepValid) {
            handleNextStep();
          }
          return;
        }
        // Only allow form submission on final step or single-step forms
        handleSubmit(handleFormSubmit)(e);
      }} 
      id={id}
      onKeyDown={(e) => {
        // Prevent form submission on Enter key if not on final step
        if (e.key === 'Enter' && isMultiStep && currentStep < totalSteps) {
          e.preventDefault();
          // Optionally, trigger next step on Enter if current step is valid
          if (isCurrentStepValid) {
            handleNextStep();
          }
        }
      }}
    >
      {/* Step Navigation */}
      {isMultiStep && (
        <StepNavigation
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepChange={handleStepChange}
        />
      )}

      {/* Form Fields */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 2,
        }}
      >
        {currentStepFields.map((field) => {
          const colSpan = getColumnSpan(field);
          
          return (
            <Box
              key={field.name}
              sx={{
                gridColumn: `span ${colSpan}`,
              }}
            >
              <FieldRenderer
                field={field}
                control={control}
                errors={errors}
                baseUrl={baseUrl}
                apiHeaders={apiHeaders}
                formData={watchedValues}
              />
            </Box>
          );
        })}
      </Box>
      
      {/* Buttons */}
      {renderButtons()}
    </Box>
  );

  // Wrap with theme provider if custom theme is provided
  if (customTheme) {
    return <ThemeProvider theme={customTheme}>{formContent}</ThemeProvider>;
  }

  return formContent;
}; 